import dayjs from 'dayjs';

/**
 * Checks if the given date and time is more than 30 minutes ago.
 * 
 * @param dateTime - The date and time to compare.
 * @returns `true` if the date is more than 30 minutes ago, otherwise `false`.
 */
export const isDateTimeGreaterThan30MinutesAgo = (dateTime: string): boolean => {
    const currentDateTime = dayjs();
    const inputDateTime = dayjs(dateTime, 'DD.MM.YYYY hh:mm A');

    return currentDateTime.diff(inputDateTime, 'minute') < 30;
};

/**
 * Format date and time in standard format: DD/MM/YYYY, HH:MM AM/PM
 * 
 * @param dateString - The date string to format
 * @returns Formatted date and time string
 */
export const formatDateTime = (dateString: string): string => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '--';
    
    return date.toLocaleString("en-pk", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
};

/**
 * Format date and time with short month name: DD MMM YYYY, HH:MM AM/PM
 * 
 * @param dateString - The date string to format
 * @returns Formatted date and time string with short month
 */
export const formatDateTimeWithMonth = (dateString: string): string => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '--';
    
    return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
};

/**
 * Format date only in ISO format: YYYY-MM-DD
 * 
 * @param date - The date to format
 * @returns Formatted date string in ISO format
 */
export const formatDateISO = (date: Date): string => {
    return date.toISOString().split("T")[0];
};

/**
 * Format date for display: DD/MM/YYYY
 * 
 * @param dateString - The date string to format
 * @returns Formatted date string
 */
export const formatDateOnly = (dateString: string | Date): string => {
    if (!dateString) return '--';
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    if (isNaN(date.getTime())) return '--';
    
    return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
};

/**
 * Returns the latest reportingTime from printed tests for bio-data display.
 */
export const getMaxReportingDate = (
    testResults?: Array<{ reportingTime?: string | Date | null }>,
    fallbackReportingDate?: string | Date | null
): string | null => {
    let maxTime: number | null = null;

    testResults?.forEach((test) => {
        if (!test?.reportingTime) return;
        const time = new Date(test.reportingTime).getTime();
        if (!isNaN(time) && (maxTime === null || time > maxTime)) {
            maxTime = time;
        }
    });

    if (maxTime !== null) {
        return new Date(maxTime).toISOString();
    }

    if (fallbackReportingDate) {
        const fallback = new Date(fallbackReportingDate).getTime();
        if (!isNaN(fallback)) {
            return new Date(fallbackReportingDate).toISOString();
        }
    }

    return null;
};
